/**
 * The Decision Room — the screen.
 *
 * The desk lists the decisions the platform's records put in front of the
 * chief executive, nearest decide-by first. Opening one shows the stakes,
 * the options side by side, the stress test, what would flip the answer, the
 * three lines for the board, and the place to record what was decided. The
 * arithmetic lives in `domain/decisionRoom.ts` and, beneath it, the Decision
 * Making Assist engine, which is still reachable as "the engine". A
 * connected model may argue, and every figure it uses is checked.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { askModel, hasModel, loadConfig } from "../../assistant/external";
import type { CriticalReport } from "../../domain/capacity";
import {
  defaultConstraints,
  defaultRateCard,
  runEngine,
  verifyFigures,
  type Constraints,
  type EngineInputs,
  type RateCard,
  type SourceRef,
} from "../../domain/decision";
import {
  answerInRoom,
  buildDesk,
  materialForRoom,
  ROOM_MODEL_PROMPT,
  type Desk,
  type RoomDecision,
  type RoomEntered,
  type RoomOption,
} from "../../domain/decisionRoom";
import { money } from "../../domain/intelligence";
import type { Kpi } from "../../domain/kpis";
import type { LiabilityCurve } from "../../domain/liability";
import type { Programme } from "../../domain/programme";
import { Button, Card, ErrorState, Field, Input, Select, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { useIntelligence, type Gathered } from "../intelligence/gather";
import { ConstraintsEditor, RateCardEditor, useRecords, type Records } from "./DecisionAssistPage";
import * as roomStore from "./roomStore";
import { answerMatter, evaluateMatter, impactForDecision, materialForMatter, type Matter, type Piece } from "../../domain/ceoRoom";
import { MattersTab, summaryLines } from "./MattersTab";
import { StrategyBoard } from "./StrategyBoard";
import c from "./CeoRoom.module.css";
import * as store from "./store";
import s from "./DecisionRoom.module.css";
import { useScreenContext } from "../../assistant/screen";
import { t, tn } from "../../i18n";

/** A translated sentence with some of its values set in markup: the template keeps one word order per language. */
function rich(template: string, nodes: Record<string, ReactNode>): ReactNode {
  return template.split(/(\{\w+\})/).map((part, i) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1];
    return <Fragment key={i}>{name && name in nodes ? nodes[name] : part}</Fragment>;
  });
}

interface RoomRecords {
  curve: LiabilityCurve | null;
  programme: Programme | null;
  critical: CriticalReport | null;
  kpis: Kpi[];
  entered: RoomEntered;
  log: roomStore.RoomRecord[];
  matters: Matter[];
  board: roomStore.SavedBoard | null;
}

function useRoomRecords(enabled: boolean) {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  return useQuery({
    queryKey: ["decision-room", siteId],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<RoomRecords> => {
      const now = new Date();
      const quiet = async <T,>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const fy = now.getFullYear() + 1;
      const [curve, programme, critical, kpis, entered, log, matters, board] = await Promise.all([
        quiet(source.liabilityCurve(siteId, fy), null),
        quiet(source.programme(siteId, fy), null),
        quiet(
          source.criticalPositions(siteId, now.toISOString(), new Date(now.getTime() + 90 * 86_400_000).toISOString()),
          null,
        ),
        quiet(source.listKpis(siteId), [] as Kpi[]),
        roomStore.loadRoomEntered(source, siteId),
        roomStore.loadRoomLog(source, siteId),
        roomStore.loadMatters(source, siteId),
        roomStore.loadBoard(source, siteId),
      ]);
      return { curve, programme, critical, kpis, entered, log, matters, board };
    },
  });
}

export function DecisionRoomPage() {
  const session = useSession();
  const gathered = useIntelligence();
  const records = useRecords(gathered.data);
  const roomRecords = useRoomRecords(!!gathered.data);
  const pending = gathered.isPending || (gathered.data && (records.isPending || roomRecords.isPending));
  return (
    <Page
      eyebrow={`${session.site.code} · ${t("Executive")}`}
      title="Decision Room"
      actions={
        <>
          <Link to="/decision-assist/engine" className={s.engineLink}>
            {t("The engine")}
          </Link>
          <Button variant="primary" onClick={() => window.print()}>
            {t("Print for the board")}
          </Button>
        </>
      }
    >
      <p className={s.lead}>
        {t(
          "Where you weigh the big calls. The platform puts its decisions on your desk; you can bring any matter of your own, weigh it your way, move everything on the strategy board, and record what you decided.",
        )}
      </p>
      {pending && <Skeleton rows={8} />}
      {gathered.isError && <ErrorState error={gathered.error} retry={() => void gathered.refetch()} />}
      {records.isError && <ErrorState error={records.error} retry={() => void records.refetch()} />}
      {gathered.data && records.data && roomRecords.data && (
        <Room g={gathered.data} r={records.data} rr={roomRecords.data} />
      )}
    </Page>
  );
}

function Room({ g, r, rr }: { g: Gathered; r: Records; rr: RoomRecords }) {
  const session = useSession();
  const qc = useQueryClient();
  const source = session.source;
  const siteId = session.site.id;
  const who = session.principal.name;
  const [rateCard, setRateCard] = useState<RateCard>(r.rateCard ?? defaultRateCard());
  const [constraints, setConstraints] = useState<Constraints>(r.constraints ?? defaultConstraints(new Date()));
  const [entered, setEntered] = useState<RoomEntered>(rr.entered);
  const [log, setLog] = useState<roomStore.RoomRecord[]>(rr.log);
  const [savedTo, setSavedTo] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const desk = useMemo<Desk>(() => {
    const now = new Date();
    const engine: EngineInputs = {
      now,
      report: g.report,
      workOrders: g.inputs.workOrders,
      closures: r.closures,
      closureCosts: r.closureCosts,
      liability: r.liability,
      testTypes: r.testTypes,
      histories: r.histories,
      rateCard,
      entered: r.entered,
      constraints,
      resolvedPredictions: r.resolvedPredictions,
    };
    return buildDesk({
      now,
      siteCode: session.site.code,
      engine,
      out: runEngine(engine),
      curve: rr.curve,
      programme: rr.programme,
      critical: rr.critical,
      kpis: rr.kpis,
      compliance: g.inputs.compliance,
      closures: r.closures,
      closureCosts: r.closureCosts,
      workOrders: g.inputs.workOrders,
      entered,
    });
  }, [g, r, rr, rateCard, constraints, entered, session.site.code]);

  const open = desk.decisions.find((d) => d.id === selected) ?? desk.decisions[0];
  const [tab, setTab] = useState<RoomTab>(() => readTab());
  const [matters, setMatters] = useState<Matter[]>(rr.matters);
  const [openMatter, setOpenMatter] = useState<string | null>(rr.matters[0]?.id ?? null);
  const go = (next: RoomTab) => {
    setTab(next);
    try {
      sessionStorage.setItem(TAB_KEY, next);
    } catch {
      /* storage off: the tab is simply not remembered */
    }
  };

  /* every open decision, the desk's and the executive's own, as a piece on the board */
  const decidedIds = new Set(log.map((l) => l.decisionId));
  const pieces = useMemo<Piece[]>(() => {
    const now = Date.now();
    const deskPieces = desk.decisions
      .filter((d) => !decidedIds.has(d.id))
      .map((d) => {
        const rec = d.options.find((o) => o.key === d.recommended);
        const exposure = Math.max(0, ...d.options.map((o) => o.exposure));
        return {
          id: d.id,
          title: d.title,
          kind: "desk" as const,
          frame: d.frame,
          cost: rec?.cost ?? null,
          decideByDays: d.decideBy.days,
          oneWay: d.door === "one_way",
          exposure,
          impact: impactForDecision(d.frame, d.door === "one_way", exposure, rec?.cost ?? null),
        };
      });
    const mine = matters
      .filter((m) => !m.decided)
      .map((m) => {
        const cost = [...m.options].map((o) => o.cost ?? null).find((x) => x != null) ?? null;
        return {
          id: m.id,
          title: m.title,
          kind: "matter" as const,
          frame: "matter",
          cost,
          decideByDays: m.decideBy ? Math.ceil((new Date(m.decideBy).getTime() - now) / 86_400_000) : null,
          oneWay: false,
          exposure: 0,
          impact: m.impact,
        };
      });
    return [...deskPieces, ...mine];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desk, matters, log]);

  const saveMatters = async (next: Matter[]) => {
    setMatters(next);
    const where = await roomStore.saveMatters(source, siteId, next);
    void qc.invalidateQueries({ queryKey: ["decision-room"] });
    return where;
  };
  const decideMatter = async (m: Matter, option: string, rationale: string, summary: string[]) => {
    const rec = roomStore.recordForMatter(m, option, who, rationale, summary);
    const where = await roomStore.appendRoomLog(source, siteId, rec);
    setLog((l) => [rec, ...l]);
    setSavedTo(
      where === "platform"
        ? t("decision recorded on the platform under {name}", { name: who })
        : t("decision kept in this browser only — the platform did not take the note"),
    );
  };

  const openM = matters.find((m) => m.id === openMatter) ?? null;
  useScreenContext(
    tab === "mine" && openM
      ? {
          route: "/decision-assist",
          title: `${t("Decision Room")} — ${openM.title}`,
          summary: matterSummary(openM),
          material: matterMaterial(openM, desk.currency),
          figures: matterFigures(openM),
          own: (q) => matterAnswer(q, openM),
          prompt: ROOM_MODEL_PROMPT,
          worksInFull: true,
          offers: [
            { label: t("Which is best?"), ask: "which is best?" },
            { label: t("What would change it?"), ask: "what would change the answer?" },
            { label: t("The case against"), ask: "what is the case against the leader?" },
          ],
        }
      : open
        ? {
            route: "/decision-assist",
            title: `${t("Decision Room")} — ${open.title}`,
            summary: open.boardLine,
            material: materialForRoom(open, desk.currency),
            figures: desk.figures,
            own: (q) => answerInRoom(q, open, desk.currency),
            prompt: ROOM_MODEL_PROMPT,
            worksInFull: true,
            offers: [
              { label: t("Which option is robust?"), ask: "which option is the most robust?" },
              { label: t("What would change the answer?"), ask: "what would change the answer?" },
              { label: t("The case against"), ask: "what is the case against the recommendation?" },
            ],
          }
        : null,
  );

  const saveEntered = async (next: RoomEntered) => {
    const stamped = { ...next, enteredBy: who, enteredAt: new Date().toISOString() };
    setEntered(stamped);
    const where = await roomStore.saveRoomEntered(source, siteId, stamped);
    setSavedTo(
      where === "platform"
        ? t("saved on the platform, under your name")
        : t("saved in this browser only — the platform did not take the note"),
    );
    void qc.invalidateQueries({ queryKey: ["decision-room"] });
  };
  const persist = async (what: "card" | "constraints", value: RateCard | Constraints) => {
    const where =
      what === "card"
        ? await store.saveRateCard(source, siteId, value as RateCard)
        : await store.saveConstraints(source, siteId, value as Constraints);
    setSavedTo(
      where === "platform"
        ? t("saved on the platform, under your name")
        : t("saved in this browser only — the platform did not take the note"),
    );
    void qc.invalidateQueries({ queryKey: ["decision-records"] });
  };
  const decide = async (d: RoomDecision, option: string, rationale: string) => {
    const rec = roomStore.recordFor(d, option, who, rationale);
    const where = await roomStore.appendRoomLog(source, siteId, rec);
    setLog((l) => [rec, ...l]);
    setSavedTo(
      where === "platform"
        ? t("decision recorded on the platform under {name}", { name: who })
        : t("decision kept in this browser only — the platform did not take the note"),
    );
  };

  const TABS: { key: RoomTab; n: number; title: string; what: string; count?: number }[] = [
    { key: "desk", n: 1, title: t("Today's decisions"), what: t("What the airport's records put on your desk"), count: desk.decisions.length },
    { key: "mine", n: 2, title: t("Your own matters"), what: t("Bring anything, add your data, weigh it your way"), count: matters.length },
    { key: "board", n: 3, title: t("Strategy board"), what: t("Move issues like pieces and see the result"), count: pieces.length },
    { key: "record", n: 4, title: t("Decision record"), what: t("What was decided, when, by whom, and why"), count: log.length },
  ];

  return (
    <div className={`${s.room} decision-room`}>
      <nav className={c.tabs} aria-label={t("Decision Room sections")}>
        {TABS.map((x) => (
          <button
            key={x.key}
            type="button"
            className={`${c.tab} ${tab === x.key ? c.tabOn : ""}`}
            aria-pressed={tab === x.key}
            onClick={() => go(x.key)}
            data-testid={`room-tab-${x.key}`}
          >
            <span className={c.tabN}>{x.n}</span>
            <span className={c.tabTitle}>
              {x.title}
              {x.count ? <span className={c.tabCount}>{x.count}</span> : null}
            </span>
            <span className={c.tabWhat}>{x.what}</span>
          </button>
        ))}
      </nav>
      {savedTo && <p className={s.saved}>{savedTo}</p>}

      {tab === "desk" && (
        <>
          <section className={s.summary} aria-label={t("The desk in numbers")}>
            <Tile
              n={desk.summary.open}
              label={t("decisions on the desk")}
              sub={tn(desk.summary.oneWay, "{n} is hard to undo", "{n} are hard to undo")}
            />
            <Tile
              n={desk.summary.nearestDays ?? "—"}
              label={t("days to the nearest deadline")}
              sub={desk.decisions[0]?.title ?? ""}
            />
            <Tile
              n={money(desk.summary.moneyInPlay, desk.currency)}
              label={t("cash in the recommended options")}
              sub={t("closure hours priced separately")}
            />
            <Tile
              n={log.length}
              label={t("decisions recorded")}
              sub={log[0] ? t("latest {date} by {name}", { date: log[0].at.slice(0, 10), name: log[0].by }) : t("none yet")}
            />
          </section>

          <p className={c.glossary}>
            {rich(t("{hard} means it cannot easily be reversed once done. {holds} means it still works in every bad case the room tried."), {
              hard: <b>{t("Hard to undo")}</b>,
              holds: <b>{t("Holds up")}</b>,
            })}
          </p>

          <section className={s.desk} aria-label={t("The desk")}>
            {desk.decisions.map((d) => (
              <DeskCard
                key={d.id}
                d={d}
                currency={desk.currency}
                open={d.id === open?.id}
                onOpen={() => setSelected(d.id)}
                decided={log.some((l) => l.decisionId === d.id)}
              />
            ))}
          </section>

          {open && (
            <RoomView
              key={open.id}
              d={open}
              desk={desk}
              onEstimate={(key, est) =>
                void saveEntered({
                  ...entered,
                  estimates: { ...entered.estimates, [key]: { ...entered.estimates?.[key], ...est } },
                })
              }
              onDecide={(option, rationale) => void decide(open, option, rationale)}
            />
          )}

          <details className={s.drawer}>
            <summary>{t("Assumptions — the dates, the quarter and the rate card every figure above rests on")}</summary>
            <div className={s.drawerBody}>
              <DatesForm entered={entered} onSave={(e) => void saveEntered({ ...entered, ...e })} />
              <ConstraintsEditor
                c={constraints}
                onChange={setConstraints}
                onSave={() => void persist("constraints", constraints)}
              />
              <RateCardEditor
                card={rateCard}
                onChange={setRateCard}
                onSave={() => void persist("card", { ...rateCard, enteredBy: who, enteredAt: new Date().toISOString() })}
              />
            </div>
          </details>

          <LogView log={log} />
        </>
      )}

      {tab === "mine" && (
        <MattersTab
          matters={matters}
          currency={desk.currency}
          who={who}
          openId={openMatter}
          onOpen={setOpenMatter}
          onSave={saveMatters}
          onDecide={decideMatter}
        />
      )}

      {tab === "board" && (
        <StrategyBoard
          pieces={pieces}
          saved={rr.board}
          currency={desk.currency}
          onSave={(b) => roomStore.saveBoard(source, siteId, { ...b, by: who })}
          onOpen={(p) => {
            if (p.kind === "matter") {
              setOpenMatter(p.id);
              go("mine");
            } else {
              setSelected(p.id);
              go("desk");
            }
          }}
        />
      )}

      {tab === "record" && <LogView log={log} />}
    </div>
  );
}

type RoomTab = "desk" | "mine" | "board" | "record";
const TAB_KEY = "sky.decisionRoom.tab";
function readTab(): RoomTab {
  try {
    const v = sessionStorage.getItem(TAB_KEY);
    return v === "mine" || v === "board" || v === "record" ? v : "desk";
  } catch {
    return "desk";
  }
}

/* the open matter, as Sky reads it */
const matterSummary = (m: Matter) => summaryLines(m, evaluateMatter(m));
const matterMaterial = (m: Matter, currency: string) => materialForMatter(m, evaluateMatter(m), currency);
const matterFigures = (m: Matter) => evaluateMatter(m).figures;
const matterAnswer = (q: string, m: Matter) => answerMatter(q, m, evaluateMatter(m));

function Tile({ n, label, sub }: { n: number | string; label: string; sub: string }) {
  return (
    <div className={s.tile}>
      <div className={s.tileN}>{n}</div>
      <div className={s.tileLabel}>{label}</div>
      <div className={s.tileSub}>{sub}</div>
    </div>
  );
}

const FRAME_LABEL: Record<RoomDecision["frame"], string> = {
  runway: "Airfield",
  capital: "Capital",
  peak: "Readiness",
  regulator: "Regulator",
  quarter: "The quarter",
};

function DeskCard({
  d,
  currency,
  open,
  onOpen,
  decided,
}: {
  d: RoomDecision;
  currency: string;
  open: boolean;
  onOpen: () => void;
  decided: boolean;
}) {
  const rec = d.options.find((o) => o.key === d.recommended);
  const robust = d.robust === d.recommended;
  return (
    <button
      type="button"
      className={`${s.card} ${open ? s.cardOpen : ""}`}
      onClick={onOpen}
      aria-pressed={open}
      data-testid={`desk-card-${d.id}`}
    >
      <div className={s.cardTop}>
        <span className={s.frame}>{t(FRAME_LABEL[d.frame])}</span>
        <span className={`${s.door} ${d.door === "one_way" ? s.oneWay : ""}`}>
          {d.door === "one_way" ? t("hard to undo") : t("can be undone")}
        </span>
        {decided && <span className={s.decided}>{t("decided")}</span>}
      </div>
      <h3 className={s.cardTitle}>{d.title}</h3>
      <p className={s.cardQ}>{d.question}</p>
      <div className={s.cardFoot}>
        <span className={s.by}>{rich(t("decide in {n} days"), { n: <b>{d.decideBy.days ?? "—"}</b> })}</span>
        {rec ? (
          <span className={s.recLine}>
            {rec.name}
            {rec.cost != null ? ` · ${money(Math.round(rec.cost), currency)}` : ""}
            <i className={robust ? s.robustYes : s.robustNo}>
              {robust ? ` · ${t("holds up")}` : ` · ${t("may not hold up in a bad case")}`}
            </i>
          </span>
        ) : (
          <span className={s.recLine}>{t("no option priced yet")}</span>
        )}
      </div>
    </button>
  );
}

function RoomView({
  d,
  desk,
  onEstimate,
  onDecide,
}: {
  d: RoomDecision;
  desk: Desk;
  onEstimate: (key: string, est: { cost?: number; closureHours?: number; days?: number }) => void;
  onDecide: (option: string, rationale: string) => void;
}) {
  const cur = desk.currency;
  return (
    <article className={s.open} data-testid={`room-${d.id}`} aria-label={d.title}>
      <header className={s.openHead}>
        <div className={s.openEyebrow}>
          {t(FRAME_LABEL[d.frame])} · {t("decide by {date}", { date: d.decideBy.at?.slice(0, 10) ?? "—" })} ·{" "}
          {d.decideBy.text}
        </div>
        <h2 className={s.question}>{d.question}</h2>
        <p className={s.onDesk}>
          <b>{t("Why it is here:")}</b> {d.onDesk} <b>{t("Can it be undone?")}</b>{" "}
          {d.door === "one_way" ? t("Hard to undo") : t("Yes")} — {d.doorWhy}
        </p>
      </header>

      <QuickAnswer d={d} currency={cur} />

      <section className={s.stakes} aria-label={t("The stakes")}>
        {d.stakes.map((st) => (
          <div key={st.key} className={`${s.stake} ${st.assumed ? s.assumed : ""}`}>
            <div className={s.stakeN}>
              {st.value == null
                ? "—"
                : typeof st.value === "number" && st.unit === cur
                  ? money(st.value, cur)
                  : st.value}
              {st.value != null && st.unit && st.unit !== cur && <small> {st.unit}</small>}
            </div>
            <div className={s.stakeLabel}>{st.label}</div>
            <div className={s.stakeText}>{st.text}</div>
            {st.assumed && <span className={s.tag}>{t("rate card / entered")}</span>}
            <Refs list={st.sources} />
          </div>
        ))}
      </section>

      <section className={s.options} aria-label={t("The options")}>
        {d.options.map((o) => (
          <OptionCard
            key={o.key}
            o={o}
            d={d}
            currency={cur}
            onEstimate={(est) => onEstimate(`${d.frame}:${o.key}`, est)}
          />
        ))}
      </section>

      <div className={s.twoCol}>
        <Card title="Our advice" sub="ranked on the room's arithmetic, with the strongest case against it">
          <ol className={s.chain}>
            {d.reasoning.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
          {d.counter && (
            <p className={s.counter}>
              <b>{t("Against it:")}</b> {d.counter}
            </p>
          )}
        </Card>
        <Card title="What would change the answer" sub="the input and the point at which another option wins">
          {d.flips.length === 0 && (
            <p className={s.muted}>
              {t(
                "No flip point: the recommendation holds across every sweep the room tried, or no second option is priced.",
              )}
            </p>
          )}
          <ul className={s.flips}>
            {d.flips.map((f) => (
              <li key={f.input}>
                {rich(t("{input} is {now}; {at}, {outcome}."), {
                  input: <b>{f.input}</b>,
                  now: f.now,
                  at: f.at,
                  outcome: <em>{f.outcome}</em>,
                })}
              </li>
            ))}
          </ul>
          {d.gaps.length > 0 && (
            <>
              <div className={s.colTitle}>{t("What the platform could not supply")}</div>
              <ul className={s.plain}>
                {d.gaps.map((gp) => (
                  <li key={gp}>{gp}</li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      <Card title="How each choice holds up in bad cases" sub={d.robustNote}>
        <div className={s.gridWrap}>
          <table className={s.grid}>
            <thead>
              <tr>
                <th>{t("option")}</th>
                {d.scenarios.map((sc) => (
                  <th key={sc.key} title={sc.what}>
                    {sc.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.options.map((o) => (
                <tr key={o.key}>
                  <td className={s.gridOpt}>
                    {o.name}
                    {o.key === d.recommended && <span className={s.miniTag}>{t("recommended")}</span>}
                    {o.key === d.robust && <span className={s.miniTagGreen}>{t("holds up")}</span>}
                  </td>
                  {d.scenarios.map((sc) => {
                    const c = d.stress.find((x) => x.option === o.key && x.scenario === sc.key)!;
                    return (
                      <td key={sc.key} className={`${s.cell} ${s[c.verdict]}`} title={c.text} data-testid="stress-cell">
                        <b>{t(c.verdict)}</b>
                        {c.ratio != null && <span>{c.ratio}×</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className={s.legend}>
          {d.scenarios.map((sc) => (
            <li key={sc.key}>
              <b>{sc.name}.</b> {sc.what}
              {sc.sources.length > 0 && <Refs list={sc.sources} />}
            </li>
          ))}
        </ul>
      </Card>

      {d.ripples.length > 0 && (
        <Card title="What else it affects" sub="knock-on effects the records show">
          <ul className={s.plain}>
            {d.ripples.map((rp) => (
              <li key={rp}>{rp}</li>
            ))}
          </ul>
        </Card>
      )}

      <BoardLine d={d} desk={desk} />
      <DecideForm d={d} onDecide={onDecide} />

      <Card
        title="Evidence"
        sub={tn(
          d.evidence.length,
          "{n} record cited — each opens on the platform",
          "{n} records cited — each opens on the platform",
        )}
      >
        <Refs list={d.evidence} wide />
      </Card>
    </article>
  );
}

/** The answer first: the question, the advice, the deadline and whether it can be undone, in one strip. */
function QuickAnswer({ d, currency }: { d: RoomDecision; currency: string }) {
  const rec = d.options.find((o) => o.key === d.recommended);
  return (
    <section className={c.quickAnswer} aria-label={t("The short answer")} data-testid="quick-answer">
      <div className={c.qaItem}>
        <span className={c.qaLabel}>{t("Our advice")}</span>
        <span className={c.qaValue}>{rec ? rec.name : t("Not enough priced to advise")}</span>
        {rec && (
          <span className={c.qaSub}>
            {rec.cost != null ? money(Math.round(rec.cost), currency) : t("not priced")}
            {d.robust === d.recommended ? ` · ${t("holds up in every bad case tried")}` : ""}
          </span>
        )}
      </div>
      <div className={c.qaItem}>
        <span className={c.qaLabel}>{t("Decide by")}</span>
        <span className={c.qaValue}>{d.decideBy.at?.slice(0, 10) ?? "—"}</span>
        <span className={c.qaSub}>{d.decideBy.days != null ? tn(d.decideBy.days, "{n} day left", "{n} days left") : ""}</span>
      </div>
      <div className={c.qaItem}>
        <span className={c.qaLabel}>{t("Can it be undone?")}</span>
        <span className={c.qaValue}>{d.door === "one_way" ? t("Hard to undo") : t("Yes")}</span>
        <span className={c.qaSub}>{tn(d.options.length, "{n} choice to compare", "{n} choices to compare")}</span>
      </div>
      <div className={c.qaItem}>
        <span className={c.qaLabel}>{t("What could change it")}</span>
        <span className={c.qaValue}>{d.flips[0] ? d.flips[0].input : t("Nothing the room tried")}</span>
        {d.flips[0] && <span className={c.qaSub}>{d.flips[0].at}</span>}
      </div>
    </section>
  );
}

function OptionCard({
  o,
  d,
  currency,
  onEstimate,
}: {
  o: RoomOption;
  d: RoomDecision;
  currency: string;
  onEstimate: (est: { cost?: number; closureHours?: number; days?: number }) => void;
}) {
  const rec = o.key === d.recommended;
  const robust = o.key === d.robust;
  const [est, setEst] = useState<{ cost: string; closureHours: string; days: string }>({
    cost: "",
    closureHours: "",
    days: "",
  });
  return (
    <div
      className={`${s.option} ${rec ? s.recommended : ""} ${robust ? s.robustOpt : ""}`}
      data-testid={`option-${o.key}`}
    >
      <div className={s.optTop}>
        {rec && <span className={s.miniTag}>{t("recommended")}</span>}
        {robust && <span className={s.miniTagGreen}>{t("holds up")}</span>}
        <span className={`${s.door} ${o.door === "one_way" ? s.oneWay : ""}`}>
          {o.door === "one_way" ? t("hard to undo") : t("can be undone")}
        </span>
      </div>
      <h3 className={s.optName}>{o.name}</h3>
      <p className={s.optMeaning}>{o.meaning}</p>
      <div className={s.optBig}>
        <span>{o.cost != null ? money(Math.round(o.cost), currency) : t("not priced")}</span>
        <span>{o.closureHours != null ? t("{n} h closed", { n: o.closureHours }) : t("closure unknown")}</span>
        <span>{t("exposure {n}", { n: o.exposure })}</span>
      </div>
      <dl className={s.metrics}>
        {o.metrics.map((m) => (
          <div key={m.label}>
            <dt>{m.label}</dt>
            <dd>
              {m.value}
              {m.assumed && <span className={s.tagInline}>{t("rate card")}</span>}
            </dd>
          </div>
        ))}
      </dl>
      <div className={s.forAgainst}>
        <div>
          <div className={s.colTitle}>{t("For")}</div>
          <ul className={s.plain}>
            {o.forCase.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className={s.colTitle}>{t("Against")}</div>
          <ul className={s.plain}>
            {o.againstCase.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>
      </div>
      {o.displaces.length > 0 && (
        <p className={s.displaces}>
          <b>{t("Displaces")}</b> {o.displaces.join(", ")}
        </p>
      )}
      {o.unpriced && (
        <form
          className={s.estimate}
          onSubmit={(e) => {
            e.preventDefault();
            onEstimate({
              cost: est.cost ? Number(est.cost) : undefined,
              closureHours: est.closureHours ? Number(est.closureHours) : undefined,
              days: est.days ? Number(est.days) : undefined,
            });
          }}
        >
          <p className={s.muted}>{o.unpriced}</p>
          <div className={s.estimateRow}>
            <Field label={t("estimate ({currency})", { currency })}>
              <Input
                type="number"
                min={0}
                value={est.cost}
                onChange={(e) => setEst({ ...est, cost: e.target.value })}
                aria-label={t("estimated cost of {name}", { name: o.name })}
              />
            </Field>
            <Field label="closure hours">
              <Input
                type="number"
                min={0}
                value={est.closureHours}
                onChange={(e) => setEst({ ...est, closureHours: e.target.value })}
              />
            </Field>
            <Field label="days">
              <Input
                type="number"
                min={0}
                value={est.days}
                onChange={(e) => setEst({ ...est, days: e.target.value })}
              />
            </Field>
            <Button type="submit" size="sm" disabled={!est.cost}>
              {t("Enter the estimate")}
            </Button>
          </div>
        </form>
      )}
      <Refs list={o.sources} />
    </div>
  );
}

function BoardLine({ d, desk }: { d: RoomDecision; desk: Desk }) {
  const cfg = useMemo(() => loadConfig(), []);
  const connected = hasModel(cfg);
  const [busy, setBusy] = useState(false);
  const [modelText, setModelText] = useState<string | null>(null);
  const [modelNote, setModelNote] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [answers, setAnswers] = useState<{ q: string; a: string; from: string }[]>([]);
  const material = materialForRoom(d, desk.currency);

  const challenge = async () => {
    setBusy(true);
    try {
      const reply = await askModel(
        cfg,
        ROOM_MODEL_PROMPT,
        `Argue against the room's recommendation, then say what you would decide and why.\n\n${material}`,
      );
      const bad = verifyFigures(reply.text, desk.figures);
      if (bad.length) {
        setModelText(null);
        setModelNote(
          bad.length === 1
            ? t("The model's challenge was withheld: it stated a figure the room did not produce ({figures}).", {
                figures: bad.slice(0, 4).join(", "),
              })
            : t("The model's challenge was withheld: it stated figures the room did not produce ({figures}).", {
                figures: bad.slice(0, 4).join(", "),
              }),
        );
      } else {
        setModelText(reply.text);
        setModelNote(t("Challenged by {model}; every figure checked against the room.", { model: reply.model }));
      }
    } catch (e) {
      setModelNote(t("The model could not be reached: {error}", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };
  const ask = async () => {
    const text = q.trim();
    if (!text) return;
    const own = answerInRoom(text, d, desk.currency);
    if (own) {
      setAnswers((a) => [{ q: text, a: own, from: t("the room") }, ...a]);
      setQ("");
      return;
    }
    if (!connected) {
      setAnswers((a) => [
        {
          q: text,
          a: t(
            "The room answers questions about an option, the recommendation, the case against it, what is robust, what would flip the answer, what is displaced, and the board line. Connect a model in Settings for anything wider.",
          ),
          from: t("the room"),
        },
        ...a,
      ]);
      setQ("");
      return;
    }
    setBusy(true);
    try {
      const reply = await askModel(cfg, ROOM_MODEL_PROMPT, `Question: ${text}\n\nMaterial:\n${material}`);
      const bad = verifyFigures(reply.text, desk.figures);
      setAnswers((a) => [
        {
          q: text,
          a: bad.length
            ? t("Withheld: the reply stated figures the room did not produce ({figures}).", {
                figures: bad.slice(0, 4).join(", "),
              })
            : reply.text,
          from: bad.length ? t("withheld") : reply.model,
        },
        ...a,
      ]);
    } catch (e) {
      setAnswers((a) => [
        {
          q: text,
          a: t("The model could not be reached: {error}", { error: e instanceof Error ? e.message : String(e) }),
          from: t("error"),
        },
        ...a,
      ]);
    } finally {
      setBusy(false);
      setQ("");
    }
  };

  return (
    <Card title="The line for the board" sub="three sentences, written from the room's figures">
      <div className={s.board}>
        {d.boardLine.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      <div className={s.row}>
        <Button onClick={() => void challenge()} disabled={!connected || busy}>
          {connected
            ? busy
              ? t("Asking…")
              : t("Ask the model to argue against it")
            : t("No model connected — Settings › Sky › beyond the platform")}
        </Button>
        {modelNote && <span className={s.muted}>{modelNote}</span>}
      </div>
      {modelText && <blockquote className={s.model}>{modelText}</blockquote>}
      <form
        className={s.ask}
        onSubmit={(e) => {
          e.preventDefault();
          void ask();
        }}
      >
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Which option is robust? What would change the answer? What does the bundle displace?")}
          aria-label={t("Question for the room")}
        />
        <Button type="submit" variant="primary" disabled={busy || !q.trim()}>
          {t("Ask")}
        </Button>
      </form>
      {answers.length > 0 && (
        <div className={s.answers}>
          {answers.map((a, i) => (
            <div key={i} className={s.answer}>
              <div className={s.q}>{a.q}</div>
              <p className={s.a}>{a.a}</p>
              <div className={s.from}>{a.from}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function DecideForm({ d, onDecide }: { d: RoomDecision; onDecide: (option: string, rationale: string) => void }) {
  const [option, setOption] = useState(d.recommended ?? d.options[0]?.key ?? "");
  const [rationale, setRationale] = useState("");
  return (
    <Card
      title="Make the decision"
      sub="recorded on the platform under your name, with the board line and the evidence as they stood"
    >
      <form
        className={s.decide}
        onSubmit={(e) => {
          e.preventDefault();
          if (!rationale.trim()) return;
          onDecide(option, rationale.trim());
          setRationale("");
        }}
      >
        <Field label={t("decision on {name}", { name: d.title })}>
          <Select
            value={option}
            onChange={(e) => setOption(e.target.value)}
            aria-label={t("decision on {name}", { name: d.title })}
          >
            {d.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="because">
          <Input
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder={t("why this, and what was accepted to get it")}
            aria-label={t("rationale for {name}", { name: d.title })}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={!rationale.trim()}>
          {t("Record the decision")}
        </Button>
      </form>
    </Card>
  );
}

function LogView({ log }: { log: roomStore.RoomRecord[] }) {
  return (
    <Card
      title="Decisions recorded"
      sub={
        log.length
          ? t("{n}, latest first — each a dated, attributed note on the platform", { n: log.length })
          : "none yet"
      }
    >
      {log.length > 0 && (
        <div className={s.log}>
          {log.map((r) => (
            <div key={r.id} className={s.logRow} data-testid="room-log-row">
              <div className={s.logHead}>
                <b>{r.title}</b> — {r.optionName}
                <span className={s.logMeta}>
                  {r.at.slice(0, 16).replace("T", " ")} · {r.by}
                </span>
              </div>
              <p className={s.logRationale}>{r.rationale}</p>
              <p className={s.muted}>{r.boardLine.join(" ")}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function DatesForm({ entered, onSave }: { entered: RoomEntered; onSave: (e: Partial<RoomEntered>) => void }) {
  const [peakName, setPeakName] = useState(entered.peakName ?? "");
  const [peakFrom, setPeakFrom] = useState(entered.peakFrom?.slice(0, 10) ?? "");
  const [auditAt, setAuditAt] = useState(entered.auditAt?.slice(0, 10) ?? "");
  return (
    <Card title="The dates" sub="the peak and the inspection, entered here because the platform does not hold them">
      <form
        className={s.dates}
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ peakName: peakName || undefined, peakFrom: peakFrom || undefined, auditAt: auditAt || undefined });
        }}
      >
        <Field label="the next peak">
          <Input
            value={peakName}
            onChange={(e) => setPeakName(e.target.value)}
            placeholder={t("Ramadan, Hajj, summer")}
            aria-label={t("peak name")}
          />
        </Field>
        <Field label="peak starts">
          <Input
            type="date"
            value={peakFrom}
            onChange={(e) => setPeakFrom(e.target.value)}
            aria-label={t("peak start date")}
          />
        </Field>
        <Field label="inspection date">
          <Input
            type="date"
            value={auditAt}
            onChange={(e) => setAuditAt(e.target.value)}
            aria-label={t("inspection date")}
          />
        </Field>
        <Button type="submit">{t("Save the dates")}</Button>
      </form>
      {entered.enteredBy && (
        <p className={s.muted}>
          {t("entered by {name}", { name: entered.enteredBy })}
          {entered.enteredAt ? t(" on {date}", { date: entered.enteredAt.slice(0, 10) }) : ""}
        </p>
      )}
    </Card>
  );
}

function Refs({ list, wide }: { list: SourceRef[]; wide?: boolean }) {
  if (!list.length) return null;
  const seen = new Set<string>();
  const rows = list.filter((e) => {
    const k = `${e.kind}:${e.ref}:${e.label}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return (
    <div className={`${s.refs} ${wide ? s.refsWide : ""}`}>
      {rows.map((e) => (
        <span
          key={`${e.kind}:${e.ref}:${e.label}`}
          className={`${s.ref} ${e.kind === "rate_card" || e.kind === "entered" ? s.refWeak : ""}`}
          title={`${e.kind.replace("_", " ")} — ${e.label}${e.enteredBy ? ` (${e.enteredBy})` : ""}`}
        >
          {e.href ? <Link to={e.href}>{e.ref}</Link> : e.ref}
          {wide && <em> {e.label}</em>}
        </span>
      ))}
    </div>
  );
}
